import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { FunctionComponent } from 'react';
import {
  DayAvailability,
  HourAvailability,
  TempDayAvailability,
} from '../../types';
import accountStore from '~/store/accountStore';
import medicalCareStore from '~/store/medicalCareStore';
import { Button, Grid } from '@mui/material';
import { Text } from '~/component/text';
import strings from '~/constants/strings';
import { checkForOverlapping, hasDataChanged } from '../helpers';
import { DaySetting } from './DaySetting';

type Props = {
  onClose: () => void;
};

const styles = {
  weeklyContainer: {
    maxHeight: '60vh',
    minWidth: 522,
  },
  saveButton: {
    borderWidth: 1,
    borderStyle: 'solid',
    maxWidth: 229,
    minHeight: 40,
  },
};

const initTimeScheduel: TempDayAvailability[] = [
  {
    days: [0],
    hours: [],
    dayOff: true,
  },
  {
    days: [1],
    hours: [],
    dayOff: true,
  },
  {
    days: [2],
    hours: [],
    dayOff: true,
  },
  {
    days: [3],
    hours: [],
    dayOff: true,
  },
  {
    days: [4],
    hours: [],
    dayOff: true,
  },
  {
    days: [5],
    hours: [],
    dayOff: true,
  },
  {
    days: [6],
    hours: [],
    dayOff: true,
  },
];

const initHolidayScheduel: TempDayAvailability = {
  days: [7],
  hours: [],
  dayOff: true,
};

const getValidHours = (
  hours: TempDayAvailability['hours'],
): HourAvailability[] => {
  return hours.filter(
    (hour) =>
      hour.from.hour !== null &&
      hour.to.hour !== null &&
      hour.from.minute !== null &&
      hour.to.minute !== null,
  ) as HourAvailability[];
};

const hasHolidayDataChanged = (
  initData: HourAvailability[],
  updatedData: TempDayAvailability,
): boolean => {
  const initialDayOff = initData.length === 0;
  if (updatedData.dayOff !== initialDayOff) {
    return true;
  }
  const toCompareText = (hour: HourAvailability) =>
    `${(hour.from.hour ?? 0) * 60 + (hour.from.minute ?? 0)}-${
      (hour.to.hour ?? 0) * 60 + (hour.to.minute ?? 0)
    }`;
  const originalHours = initData.map((hour) => toCompareText(hour));
  const newHours = getValidHours(updatedData.hours).map((hour) =>
    toCompareText(hour),
  );
  if (originalHours.length !== newHours.length) {
    return true;
  }
  return originalHours.some((hour) => !newHours.includes(hour));
};

const getNewWeeklyHours = (
  weeklyHours: TempDayAvailability[],
  dayIndex: number,
  data: Partial<TempDayAvailability>,
): TempDayAvailability[] => {
  const newWeeklyHours = [...weeklyHours];
  newWeeklyHours[dayIndex] = {
    ...newWeeklyHours[dayIndex],
    ...data,
  };
  return newWeeklyHours;
};

export const flattenTimeScheduel = (
  timeScheduel: DayAvailability[],
): TempDayAvailability[] => {
  const result: TempDayAvailability[] = JSON.parse(
    JSON.stringify(initTimeScheduel),
  );
  for (let i = 0; i < 7; i++) {
    const actualDay = i === 6 ? 0 : i + 1;
    result[i].hours =
      timeScheduel.find((day) => day.days.includes(actualDay))?.hours || [];
    result[i].dayOff = result[i].hours.length === 0;
  }
  return result;
};

export const WeekSetting: FunctionComponent<Props> = observer((props) => {
  const { onClose } = props;
  const [weeklyHours, setWeeklyHours] =
    useState<TempDayAvailability[]>(initTimeScheduel);
  const [holidayHours, setHolidayHours] =
    useState<TempDayAvailability>(initHolidayScheduel);
  const { doctor, pharmacy } = medicalCareStore;
  const businessHours = accountStore.isDoctor
    ? doctor?.businessHours
    : pharmacy?.businessHours;
  const { isLoading } = medicalCareStore;

  const isOverlapping = useMemo(() => {
    const daysToCheck = [...weeklyHours, holidayHours];
    for (let i = 0; i < daysToCheck.length; i++) {
      const day = daysToCheck[i];
      if (day.dayOff) {
        continue;
      }
      const overlap = checkForOverlapping(day.hours);
      if (overlap.length > 0) {
        return true;
      }
    }
    return false;
  }, [weeklyHours, holidayHours]);

  const isDirty = useMemo(() => {
    if (businessHours?.weekly) {
      const weeklyDirty = hasDataChanged(businessHours?.weekly, weeklyHours);
      const holidayDirty = hasHolidayDataChanged(
        businessHours?.holidays?.hours || [],
        holidayHours,
      );
      return weeklyDirty || holidayDirty;
    }
    return true;
  }, [
    weeklyHours,
    holidayHours,
    businessHours?.weekly,
    businessHours?.holidays?.hours,
  ]);

  useEffect(() => {
    if (businessHours) {
      const newWeeklyHours = flattenTimeScheduel(businessHours?.weekly);
      setWeeklyHours(newWeeklyHours);
      setHolidayHours({
        days: [7],
        hours: businessHours?.holidays?.hours || [],
        dayOff: (businessHours?.holidays?.hours || []).length === 0,
      });
    }
  }, [businessHours]);

  const updateDay = (dayIndex: number, data: Partial<TempDayAvailability>) => {
    if (dayIndex === 7) {
      setHolidayHours((prev) => ({
        ...prev,
        ...data,
      }));
      return;
    }
    const newWeeklyHours = getNewWeeklyHours(weeklyHours, dayIndex, data);
    setWeeklyHours(newWeeklyHours);
  };

  const updateWeeklyData = async () => {
    const weeklyData: DayAvailability[] = weeklyHours.map((day) => {
      return {
        days: day.days.map((day) => (day === 6 ? 0 : day + 1)),
        hours: day.dayOff ? [] : getValidHours(day.hours),
      };
    });
    const holidayData = holidayHours.dayOff
      ? []
      : getValidHours(holidayHours.hours);
    await medicalCareStore.updateWeekly(weeklyData, holidayData);
    onClose();
  };

  const onCancel = () => {
    onClose();
  };

  return (
    <Grid
      item
      xs={7}
      display={'flex'}
      className="borderTop--lightGrey"
      flexDirection={'column'}
      justifyContent={'center'}
      // sx={{ padding: '0 0 32px 32px' }}
    >
      <div className="m4" />
      <Text text={'通常営業時間'} className="fontWeight--bold font--16 m8b" />
      <Text
        text={
          '※トグルスイッチをOFF(グレイ)にすると、該当する曜日が定休日になります。'
        }
        className="font--10 m8b"
      />
      <div className="m4" />
      <Grid
        item
        xs={12}
        style={styles.weeklyContainer}
        className="overFlowYAuto pr32"
        display={'flex'}
        flexDirection={'column'}
      >
        {weeklyHours.map((day, index) => (
          <DaySetting
            key={index}
            day={day}
            dayIndex={index}
            updateDay={updateDay}
          />
        ))}
        <DaySetting
          key={7}
          day={holidayHours}
          dayIndex={7}
          updateDay={updateDay}
        />
      </Grid>
      <div className="m4" />
      <Grid item display={'flex'} flexDirection={'row'} alignItems={'center'}>
        <Button
          disabled={isLoading}
          className={`btn--secondary fullWidth font--18 fontWeight--bold mr16`}
          style={styles.saveButton}
          onClick={onCancel}
        >
          {strings.action.cancel}
        </Button>
        <Button
          disabled={isLoading || isOverlapping || !isDirty}
          className={`btn--${
            isLoading || isOverlapping || !isDirty
              ? 'primaryDisableLight'
              : 'primary'
          } fullWidth font--18 fontWeight--bold ml16`}
          style={styles.saveButton}
          onClick={updateWeeklyData}
        >
          {strings.action.save}
        </Button>
      </Grid>
    </Grid>
  );
});
